import { useEffect, useRef, useCallback } from "react";
import { useNotificationStore, type TNotification } from "./store";
import { fetchAllNotifications, markChapterNotificationAsRead } from "./services";
import { useChapterMemoStore } from "@/zapi-frontend/stores/chapterMemo";
import { useChatStore } from "@/zapi-frontend/stores/chatStore";
import { useProjectSetupStore } from "@/zapi-frontend/stores/projectSetup";
import { useAuthenticated } from "@/zapi-frontend/shared/hooks/useAuthenticated";

const SSE_URL = import.meta.env.VITE_ZAPI_API_URL + "/user/notifications/stream";

function getCurrentChapterId(): number | null {
  const searchParams = new URLSearchParams(window.location.search);
  const chapterId = searchParams.get("chapterId");
  return chapterId ? parseInt(chapterId, 10) : null;
}

export function useNotificationSSE() {
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;

  const { data: isAuthenticated, isLoading: isAuthLoading } = useAuthenticated();

  const setNotifications = useNotificationStore((state) => state.setNotifications);
  const addNotification = useNotificationStore((state) => state.addNotification);
  const markChapterMemoAsLoaded = useChapterMemoStore((state) => state.markChapterAsLoaded);
  const markChatChapterAsLoaded = useChatStore((state) => state.markChapterAsLoaded);

  const connect = useCallback(() => {
    if (eventSourceRef.current?.readyState === EventSource.OPEN) {
      // eslint-disable-next-line no-console
      console.log("[SSE] Already connected");
      return;
    }

    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      // eslint-disable-next-line no-console
      console.error("[SSE] Max reconnect attempts reached");
      return;
    }

    try {
      // eslint-disable-next-line no-console
      console.log("[SSE] Connecting to notification stream...");
      const eventSource = new EventSource(SSE_URL, {
        withCredentials: true
      });

      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        // eslint-disable-next-line no-console
        console.log("[SSE] Connected to notification stream");
        reconnectAttemptsRef.current = 0;
      };

      eventSource.onmessage = (event) => {
        try {
          const newNotification: TNotification = JSON.parse(event.data);

          const notificationProjectId =
            newNotification.projectChapter?.projectId ?? newNotification.projectId ?? newNotification.project?.id;
          // chapter_ready fired by an active prefill: the chat progress list already
          // shows it, so don't leave an unread orange dot behind.
          const isFromActiveSetup =
            newNotification.type === "chapter_ready" &&
            notificationProjectId != null &&
            !!useProjectSetupStore.getState().activeSetupTasks[String(notificationProjectId)];

          if (
            newNotification.projectChapterId &&
            (newNotification.projectChapterId === getCurrentChapterId() || isFromActiveSetup)
          ) {
            newNotification.isRead = true;

            markChapterNotificationAsRead(newNotification.projectChapterId).catch((error) => {
              // eslint-disable-next-line no-console
              console.error("[SSE] Failed to mark notification as read on backend:", error);
            });
          }

          if (newNotification.type === "chapter_ready" && newNotification.projectChapterId) {
            markChapterMemoAsLoaded(newNotification.projectChapterId);
          }
          if (newNotification.type === "chat_responded" && newNotification.projectChapterId) {
            markChatChapterAsLoaded(newNotification.projectChapterId);
          }

          addNotification(newNotification);
        } catch (error) {
          if (typeof event.data === "string" && event.data.trim() === "") return;
          // eslint-disable-next-line no-console
          console.error("[SSE] Failed to parse notification:", error);
        }
      };

      eventSource.onerror = (error) => {
        // eslint-disable-next-line no-console
        console.error("[SSE] Connection error:", error);
        eventSource.close();

        const backoffTime = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
        reconnectAttemptsRef.current++;

        // eslint-disable-next-line no-console
        console.log(`[SSE] Reconnecting in ${backoffTime / 1000}s... (attempt ${reconnectAttemptsRef.current})`);

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, backoffTime);
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[SSE] Failed to create EventSource:", error);
    }
  }, [addNotification, markChapterMemoAsLoaded, markChatChapterAsLoaded]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    if (eventSourceRef.current) {
      // eslint-disable-next-line no-console
      console.log("[SSE] Disconnecting from notification stream");
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    reconnectAttemptsRef.current = 0;
  }, []);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (!isAuthenticated) {
      disconnect();
      return;
    }

    fetchAllNotifications()
      .then(setNotifications)
      .then(connect)
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch notifications:", error);
      });

    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAuthLoading, disconnect]);
}
